import { LIST_ITEM_PREFIX_REGEX } from "utils/createHashFromText";

import normalizeAnchorText from "./normalizeAnchorText";

// The joined document is rebuilt once per paragraph list and held against that list, so
// repeated jumps through one document do not re-concatenate it.
const joinedCache = new WeakMap();

const joinParagraphs = (paragraphs) => {
  const cached = joinedCache.get(paragraphs);

  if (cached) {
    return cached;
  }

  const starts = [];
  let text = "";

  paragraphs.forEach((paragraph) => {
    if (text) {
      text += " ";
    }
    starts.push(text.length);
    text += paragraph.normalized;
  });

  const joined = { text, starts };
  joinedCache.set(paragraphs, joined);

  return joined;
};

const occurrencesOf = (haystack, needle) => {
  const found = [];
  let at = haystack.indexOf(needle);

  while (at !== -1) {
    found.push(at);
    at = haystack.indexOf(needle, at + 1);
  }

  return found;
};

// Which paragraph owns a character offset in the joined text. Binary search, because a large
// solicitation runs to tens of thousands of paragraphs and a jump should not scan them all.
const paragraphAt = (starts, offset) => {
  let low = 0;
  let high = starts.length - 1;
  let found = 0;

  while (low <= high) {
    const middle = (low + high) >> 1;

    if (starts[middle] <= offset) {
      found = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  return found;
};

// Each covered paragraph carries the slice of the quote that falls inside it, so a caller can
// highlight the quote itself rather than the whole paragraph, and can do so per paragraph when
// the quote runs across a break.
const segmentsFor = (paragraphs, starts, text, from, length) => {
  const to = from + length;
  const first = paragraphAt(starts, from);
  const last = paragraphAt(starts, to - 1);
  const segments = [];

  for (let index = first; index <= last; index += 1) {
    const paragraphStart = starts[index];
    const paragraphEnd = paragraphStart + paragraphs[index].normalized.length;
    const overlapFrom = Math.max(from, paragraphStart);
    const overlapTo = Math.min(to, paragraphEnd);

    // The single space joining two paragraphs belongs to neither of them.
    if (overlapTo <= overlapFrom) {
      continue;
    }

    segments.push({
      ...paragraphs[index],
      match: text.slice(overlapFrom, overlapTo),
    });
  }

  return segments;
};

// A quote that repeats is disambiguated by where it sits in the document, compared on the
// same 0-1 scale the caller's hint uses. With no hint the first occurrence is the honest pick.
const bestOccurrence = (occurrences, paragraphs, starts, hint) => {
  if (occurrences.length === 1 || !Number.isFinite(hint)) {
    return occurrences[0];
  }

  return occurrences.reduce((best, at) => {
    const distanceOf = (offset) =>
      Math.abs(hint - paragraphAt(starts, offset) / paragraphs.length);

    return distanceOf(at) < distanceOf(best) ? at : best;
  }, occurrences[0]);
};

const longestRunOfWords = (normalizedQuote) => {
  const words = normalizedQuote.split(" ");

  if (words.length < 6) {
    return null;
  }

  // Trim a word from each end: a quote that fails to match usually differs at its edges,
  // where the backend's sentence splitting and the renderer's disagree.
  return words.slice(1, -1).join(" ");
};

/**
 * Finds `quote` in the rendered document.
 *
 * @param {Array} documentParagraphs `{ ordinal, pageNumber, normalized }`, in document order.
 * @param {{ quote: string, hint: number }} target `hint` is a 0-1 position through the
 *   document used only to choose between repeated occurrences.
 * @returns {{ segments: Array, start: object, pageNumber: number|null }|null} `segments` are
 *   the paragraphs the quote covers — more than one when it spans a paragraph break.
 */
const resolveDocumentAnchor = (documentParagraphs, { quote, hint } = {}) => {
  if (!documentParagraphs?.length || !quote) {
    return null;
  }

  const { text, starts } = joinParagraphs(documentParagraphs);

  const attempts = [];
  const normalized = normalizeAnchorText(quote);

  if (normalized) {
    attempts.push(normalized);

    // Only as a fallback: docx-preview renders auto numbering through CSS counters, so it is
    // absent from the text on both sides, and a leading number that IS present is usually a
    // real section label that both sides carry. Stripping it first would lose that.
    const withoutListPrefix = normalizeAnchorText(
      String(quote).trim().replace(LIST_ITEM_PREFIX_REGEX, "")
    );

    if (withoutListPrefix && withoutListPrefix !== normalized) {
      attempts.push(withoutListPrefix);
    }

    const trimmed = longestRunOfWords(normalized);

    if (trimmed) {
      attempts.push(trimmed);
    }
  }

  for (const attempt of attempts) {
    const occurrences = occurrencesOf(text, attempt);

    if (!occurrences.length) {
      continue;
    }

    const at = bestOccurrence(occurrences, documentParagraphs, starts, hint);
    const segments = segmentsFor(
      documentParagraphs,
      starts,
      text,
      at,
      attempt.length
    );

    return {
      segments,
      start: segments[0],
      pageNumber: segments[0]?.pageNumber ?? null,
    };
  }

  return null;
};

export default resolveDocumentAnchor;
